import React, { useEffect, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import FormControl from '@material-ui/core/FormControl';
import { FormHelperText, Input, InputLabel, CircularProgress, Box, Select, MenuItem } from '@material-ui/core';
import FormData from 'form-data';
import axios from 'axios';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { Entity } from '@backstage/catalog-model';

const CDN_API = 'platform.digando.io/v1alpha1/digandocdnapis';

const cdnLink = (entity: Entity): string =>
    (entity.spec as any)?.object?.status?.link ?? '';

const cdnTitle = (entity: Entity): string =>
    entity.metadata.title ?? entity.metadata.name;

export const CdnUploadImage = ({
    onChange,
    rawErrors,
    required,
    formData,
}: FieldExtensionComponentProps<string>) => {
    const [fileName, setFileName] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [cdnUrl, setCdnUrl] = useState<string>('')
    const [cdns, setCdns] = useState<Entity[]>([])
    const catalogApi = useApi(catalogApiRef);

    useEffect(() => {
        let cancelled = false;
        catalogApi
            .getEntities({ filter: { kind: 'Resource', 'spec.api': CDN_API } })
            .then(res => {
                if (cancelled) return;
                setCdns(res.items.filter(entity => cdnLink(entity).length > 0));
            });
        return () => {
            cancelled = true;
        };
    }, [catalogApi]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {

        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];

            const data = new FormData();
            data.append('file', file, file.name);

            const config = {
                method: 'post',
                maxContentLength: Infinity,
                maxBodyLength: Infinity,
                url: `${cdnUrl}/dato`,
                data: data,
            };
            onChange("")
            setIsLoading(true)
            const response = await axios.request(config);
            setFileName(file.name);
            onChange(response.data.id)
            setIsLoading(false)
        }
    };
    return (
        <>
            <FormControl
                margin="normal"
                required={required}
                error={rawErrors?.length > 0 && !formData}
            >
                <InputLabel htmlFor="env">CDN</InputLabel>
                <Select
                    id="env"
                    onChange={(e: any) => setCdnUrl(e.target.value)}
                >
                    {cdns.map((entity: Entity) => (
                        <MenuItem key={entity.metadata.uid ?? entity.metadata.name} value={cdnLink(entity)}>
                            {cdnTitle(entity)}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl
                margin="normal"
                required={required}
                error={rawErrors?.length > 0 && !formData}
                disabled={!(cdnUrl.length > 0)}
            >
                <InputLabel htmlFor="fileUpload">Upload File</InputLabel>
                <Input
                    id="fileUpload"
                    type="file"
                    onChange={handleFileChange}
                />
                {fileName && (
                    <FormHelperText id="fileName">
                        Uploaded file: {fileName}
                    </FormHelperText>
                )}
                {isLoading && (
                    <Box sx={{ display: 'flex', marginTop: '30px' }}>
                        <CircularProgress />
                    </Box>
                )}
            </FormControl>
        </>
    );
};

/*
 This is a validation function that will run when the form is submitted.
  You will get the value from the `onChange` handler before as the value here to make sure that the types are aligned\
*/

export const CdnUploadImageValidation = (
    value: string,
    validation: FieldValidation,
) => {
    if (value === "") {
        validation.addError(
            `Image is not uploaded yet`,
        );
    }
};
